import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import round from 'lodash/round';
import Tooltip from '@mui/material/Tooltip';
import GeneralChart from '@/components/seo/GeneralChart';
import { Liquid } from '@/components/AsyncComponent';
import { JobTitleContext } from '@/context/create-context';
import { COMPETITIVE_SCORE_INTRO } from '@/constants/job-title';
import SvgHelp from './SvgHelp';

const SalaryCity = (props: { id: string; cityData: Record<string, any> }) => {
  const { id, cityData } = props;
  const cityEffectData: string[] = cityData?.['影响因素'] || [];

  const {
    jobTitle: { locations, id: jobTitleId },
    isJobTitleCity,
  } = React.useContext(JobTitleContext);
  const router = useRouter();

  return (
    <GeneralChart
      id={id}
      title="影响薪资的核心维度4：所在城市"
      tip={cityData['tip']}
    >
      <p className="mb-2">
        {cityData['一句话总结']?.[isJobTitleCity ? '城市趋势' : '全国趋势'] ||
          ''}
      </p>
      <div className="my-4">
        <table className="w-full table-fixed text-sm text-center">
          <thead className="font-semibold">
            <tr>
              <th className="text-left p-2 pl-6">城市</th>
              <th className="p-2">职位数</th>
              <th className="p-2">平均月薪</th>
              <th className="p-2">
                城市平均月租
                <br />
                （两居室）
              </th>
              <th className="p-2 flex items-center">
                <span>谈职薪资竞争力指数</span>
                <Tooltip
                  title={
                    <div className="text-primary text-opacity-85">
                      <p className="text-sm">
                        <strong>谈职薪资竞争力指数：</strong>
                        <br />
                        指用本职位的薪资在此城市生活的舒适度，指数越高代表该职位在此城市的竞争力越高
                      </p>
                      <p className="mt-3 leading-5">
                        我们的计算方式：
                        <br />
                        {COMPETITIVE_SCORE_INTRO}
                      </p>
                    </div>
                  }
                  disableTouchListener={true}
                  placement="right"
                  className="ml-2 hidden tablet:inline-block"
                  PopperProps={{
                    sx: {
                      '.MuiTooltip-tooltip': {
                        background: '#fff',
                        boxShadow: '0px 8px 15px rgba(0, 0, 0, 0.15)',
                        borderRadius: '4px',
                        maxWidth: '430px',
                        padding: '8px 20px',
                        margin: 0,
                        marginLeft: '17px !important',
                      },
                    },
                  }}
                >
                  <i className="cursor-pointer">
                    <SvgHelp />
                  </i>
                </Tooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {locations
              .slice(0, 10)
              .map(
                (
                  {
                    name,
                    competitive_rank,
                    pinyin,
                    job_open_count,
                    avg,
                    rent_price,
                    competitive_rate,
                  },
                  index,
                ) => (
                  <tr
                    key={index}
                    className="border-b border-web-tertiary cursor-pointer transition-colors duration-200 hover:bg-denim/4 hover:font-semibold"
                    onClick={() => router.push(`/${pinyin}/${jobTitleId}/`)}
                  >
                    <td className="text-left p-2">
                      <div className="flex items-center">
                        <span
                          className={`font-semibold ${
                            competitive_rank <= 3 ? 'text-[#c2b105]' : ''
                          } mr-1 tablet:mr-2.5`}
                        >
                          {competitive_rank}
                        </span>
                        <Link
                          title={name}
                          href={`/${pinyin}/${jobTitleId}/`}
                          className="underline line-clamp-1"
                        >
                          {name}
                        </Link>
                      </div>
                    </td>
                    <td className="p-2">{job_open_count}</td>
                    <td className="p-2">{`￥${round(avg || 0, -2)}`}</td>
                    <td className="p-2">{`￥${round((rent_price || 0) * 70, -2)}`}</td>
                    <td className="p-2">
                      <Liquid value={round(competitive_rate * 100)} />
                    </td>
                  </tr>
                ),
              )}
          </tbody>
        </table>
      </div>
      {!!cityEffectData.length && (
        <>
          <p className="font-semibold mb-2">影响因素</p>
          <ul className="list-inside !ml-2 mb-2">
            {cityEffectData.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </>
      )}
    </GeneralChart>
  );
};

export default SalaryCity;
